import PropTypes from 'prop-types'
import React from 'react'
import { useInView } from 'react-intersection-observer'

import { GOODS_TYPES } from 'constants/sales'
import { LocalizationContext } from 'contexts/Localization'
import { MenuMainContext } from 'contexts/MenuMain'
import { Row, Col, BoxBase } from 'styles/components/layout'
import { Text, H1Small as H1 } from 'styles/components/typography'

import WForeverBox from '../BarAndBox/components/WForeverBox'
import { Bar, WForeverBoxWrapper, BarWrapper } from '../BarAndBox/styles'
import { Content, Chart, ColForChart } from '../styles'

import { fadeOutBlock, timeline } from './animation'
import useScrollMagic from 'hooks/useScrollMagic'

const WForeverDesktop = ({ data }) => {
  const { paths } = React.useContext(LocalizationContext)
  const { menuNode } = React.useContext(MenuMainContext)
  const offset = React.useMemo(() => menuNode.getBoundingClientRect().height, [
    menuNode,
  ])
  const { dna, wgs } = GOODS_TYPES

  const trigger = React.useRef()

  const wgsBar = React.useRef()
  const wgsBox = React.useRef()
  const dnaBar = React.useRef()
  const dnaBox = React.useRef()

  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  })

  const { ScrollMagic, isScrollMagicLoaded } = useScrollMagic({ inView: true })

  React.useEffect(() => {
    require('animation.gsap')

    if (isScrollMagicLoaded) {
      const controller = new ScrollMagic.Controller()
      const mainScene = new ScrollMagic.Scene({
        triggerHook: 0,
        triggerElement: trigger.current,
        offset: -offset,
        duration: 1600,
      }).setPin(trigger.current)

      const fadeOutScene = new ScrollMagic.Scene({
        triggerHook: 0,
        triggerElement: trigger.current,
        duration: 200,
      }).setTween(fadeOutBlock({ node: trigger.current }))

      controller.addScene([mainScene, fadeOutScene])

      return () => {
        if (isScrollMagicLoaded) {
          mainScene.removePin(true)
          controller.destroy()
        }
      }
    }
  }, [offset, isScrollMagicLoaded, ScrollMagic])

  React.useEffect(() => {
    const chartAnimation = timeline({ wgsBar, wgsBox, dnaBar, dnaBox })

    if (inView) {
      chartAnimation.play()
    }

    return () => chartAnimation.kill()
  }, [inView])

  return (
    <BoxBase ref={trigger} height={`calc(100vh - ${offset}px)`}>
      <Row ref={ref} alignItems="center" height="inherit">
        <Col width={{ xs: 1, md: 1 / 2 }}>
          <Content>
            <H1 as="h3" dangerouslySetInnerHTML={{ __html: data.title }} />
            <Text dangerouslySetInnerHTML={{ __html: data.text }} />
          </Content>
        </Col>

        <ColForChart
          width={{
            xs: 1,
            md: 1 / 2,
          }}
        >
          <Chart>
            <BarWrapper productType={wgs}>
              <Bar ref={wgsBar} />
              <WForeverBoxWrapper ref={wgsBox}>
                <WForeverBox
                  image={`${paths.cdn_boxes}/wgs-left-new`}
                  isTextBottom={true}
                  text={data[wgs]}
                  isWgs={true}
                />
              </WForeverBoxWrapper>
            </BarWrapper>

            <BarWrapper productType={dna}>
              <Bar ref={dnaBar} />
              <WForeverBoxWrapper ref={dnaBox}>
                <WForeverBox
                  image={`${paths.cdn_boxes}/dna-left-new`}
                  text={data[dna]}
                />
              </WForeverBoxWrapper>
            </BarWrapper>
          </Chart>
        </ColForChart>
      </Row>
    </BoxBase>
  )
}

WForeverDesktop.propTypes = {
  data: PropTypes.object.isRequired,
}

export default WForeverDesktop
